import { useState, useEffect, useCallback } from 'react';
import { supabase, checkConnection } from '@/lib/supabase/client';
import { ProjectTemplate } from '@/types/templates';
import { useToast } from './useToast';
import { useAuthStore } from '@/store/authStore';

export const useTemplates = () => {
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();
  const { user } = useAuthStore();

  const fetchTemplates = useCallback(async () => {
    if (!user) return;
    
    try {
      // Check connection first
      const connected = await checkConnection();
      if (!connected) {
        throw new Error('Unable to connect to database');
      }

      const { data, error: fetchError } = await supabase
        .from('project_templates')
        .select(`
          *,
          stages:project_template_stages (
            *,
            substages:project_template_substages (*)
          )
        `)
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setTemplates(data || []);
      setError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load templates';
      console.error('Error fetching templates:', err);
      setError(new Error(message));
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, user]);

  const deleteTemplate = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('project_templates')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // Optimistic update
      setTemplates(prev => prev.filter(template => template.id !== id));

      toast({
        title: 'Success',
        description: 'Template deleted successfully'
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete template';
      console.error('Failed to delete template:', err);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive'
      });
      // Refresh list on error to ensure consistency
      await fetchTemplates();
      throw err;
    }
  };

  useEffect(() => {
    if (!user) {
      setTemplates([]);
      setIsLoading(false);
      return;
    }

    let mounted = true;
    let channel: ReturnType<typeof supabase.channel>;

    const initialize = async () => {
      try {
        // Initial fetch
        await fetchTemplates();

        // Set up realtime subscription with error handling
        channel = supabase.channel('templates_changes')
          .on(
            'postgres_changes',
            { 
              event: '*', 
              schema: 'public',
              table: 'project_templates'
            },
            async (payload) => {
              if (!mounted) return;
              
              try {
                await fetchTemplates();
              } catch (err) {
                console.error('Error handling realtime update:', err);
              }
            }
          )
          .subscribe((status) => {
            if (status === 'SUBSCRIBED') {
              console.log('Subscribed to template changes');
            }
          });

      } catch (err) {
        console.error('Error initializing templates:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to initialize templates'));
          setIsLoading(false);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
      if (channel) {
        supabase.removeChannel(channel).catch(console.error);
      }
    };
  }, [fetchTemplates, user]);

  return {
    templates,
    isLoading,
    error,
    deleteTemplate,
    refetch: fetchTemplates
  };
};